import { get } from '../utils/http.ts';
import type { Options } from './wasmBridge/viewModel';

export type OptionType = 'number' | 'boolean' | 'choices';

export interface GameOptionDefinition {
  index: number;
  name: string;
  type: OptionType;
  min?: number;
  max?: number;
  choices?: string[];
  default: number | boolean;
}

export type SettingsCatalog = Record<string, GameOptionDefinition[]>;

interface RawCatalog {
  [puzzle: string]: { options?: RawOption[] };
}

interface RawOption extends GameOptionDefinition {
  exposed: boolean;
}

let catalogPromise: Promise<SettingsCatalog | null> | null = null;
let fullCatalogPromise: Promise<SettingsCatalog | null> | null = null;

/** Exposed options only — what the custom-board config UI renders. */
export function getCatalog(): Promise<SettingsCatalog | null> {
  catalogPromise ??= fetchCatalog(true);
  return catalogPromise;
}

/**
 * Full catalog including hidden options. Game-start validation must accept
 * hidden options because puzzle presets (the default boards) legitimately set
 * engine parameters the config UI does not expose (e.g. Rectangles expansion
 * factor and unique-solution flag).
 */
export function getFullCatalog(): Promise<SettingsCatalog | null> {
  fullCatalogPromise ??= fetchCatalog(false);
  return fullCatalogPromise;
}

/** Drop the cached catalog so a retry re-fetches it (BR-70). */
export function resetCatalog(): void {
  catalogPromise = null;
  fullCatalogPromise = null;
}

async function fetchCatalog(onlyExposed: boolean): Promise<SettingsCatalog | null> {
  const response = await get<RawCatalog>('participant/settings-catalog');
  if (response.error || !response.result) {
    console.error('Error fetching settings catalog:', response.error);
    return null;
  }

  const catalog: SettingsCatalog = {};
  for (const [puzzle, entry] of Object.entries(response.result)) {
    catalog[puzzle] = (entry.options ?? [])
      .filter(option => !onlyExposed || option.exposed)
      .map(option =>
        // Generous fallback bounds so an undefined min/max still can't be silly.
        option.type === 'number'
          ? { ...option, min: option.min ?? 0, max: option.max ?? 100 }
          : option
      );
  }
  return catalog;
}

export async function getOptionsFor(puzzle: string): Promise<GameOptionDefinition[]> {
  const catalog = await getCatalog();
  return catalog?.[puzzle] ?? [];
}

export function clampNumber(definition: GameOptionDefinition, value: number): number {
  if (Number.isNaN(value)) {
    return definition.min ?? definition.max ?? 0;
  }

  let clamped = value;
  if (definition.min !== undefined && clamped < definition.min) {
    clamped = definition.min;
  }
  if (definition.max !== undefined && clamped > definition.max) {
    clamped = definition.max;
  }
  return clamped;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateOptions(
  definitions: GameOptionDefinition[],
  options: Options[]
): ValidationResult {
  const errors: string[] = [];
  const byIndex = new Map(definitions.map(definition => [definition.index, definition]));
  const seen = new Set<number>();

  for (const option of options) {
    const definition = byIndex.get(option.index);

    if (!definition) {
      errors.push(`Option index ${option.index} is not configurable`);
      continue;
    }

    if (seen.has(option.index)) {
      errors.push(`Duplicate option index ${option.index}`);
      continue;
    }
    seen.add(option.index);

    switch (definition.type) {
      case 'number': {
        if (typeof option.value !== 'number') {
          errors.push(`${definition.name} must be a number`);
        } else if (definition.min !== undefined && option.value < definition.min) {
          errors.push(`${definition.name} must be at least ${definition.min}`);
        } else if (definition.max !== undefined && option.value > definition.max) {
          errors.push(`${definition.name} must be at most ${definition.max}`);
        }
        break;
      }
      case 'boolean': {
        if (typeof option.value !== 'boolean') {
          errors.push(`${definition.name} must be true or false`);
        }
        break;
      }
      case 'choices': {
        const length = definition.choices?.length ?? 0;
        if (
          typeof option.value !== 'number' ||
          !Number.isInteger(option.value) ||
          option.value < 0 ||
          option.value >= length
        ) {
          errors.push(`${definition.name} is not a valid choice`);
        }
        break;
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

export function buildOptions(
  definitions: GameOptionDefinition[],
  values: Map<number, number | boolean>
): Options[] {
  return definitions.map(definition => ({
    index: definition.index,
    value: values.get(definition.index)!,
    type: definition.type,
  }));
}

export function resolveInitialValues(
  definitions: GameOptionDefinition[],
  saved: Options[]
): Map<number, number | boolean> {
  const fields = new Map<number, number | boolean>();
  for (const definition of definitions) {
    const existing = saved.find(option => option.index === definition.index);
    fields.set(definition.index, (existing ? existing.value : definition.default) as number | boolean);
  }
  return fields;
}
